"use client";

import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import type { StudentCoBrandPayload } from "@/lib/partnerBranding";

/**
 * Loads Level 1 co-branding for the current user when they are linked in `partner_students`.
 * Subscribes to auth changes so branding appears after `setSession` (B2B provision flow).
 */
export function usePartnerStudentBranding(enabled: boolean): StudentCoBrandPayload | null {
  const [branding, setBranding] = useState<StudentCoBrandPayload | null>(null);

  useEffect(() => {
    if (!enabled) {
      setBranding(null);
      return;
    }

    let cancelled = false;

    const load = async (accessToken: string | undefined) => {
      if (!accessToken) {
        if (!cancelled) setBranding(null);
        return;
      }
      try {
        const res = await fetch("/api/partner/student-branding", {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const json = (await res.json().catch(() => ({}))) as StudentCoBrandPayload & {
          active?: boolean;
        };
        if (!cancelled) {
          if (res.ok && json?.active) setBranding(json);
          else setBranding(null);
        }
      } catch {
        if (!cancelled) setBranding(null);
      }
    };

    void supabase.auth.getSession().then(({ data: { session } }) => {
      void load(session?.access_token);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      void load(session?.access_token);
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [enabled]);

  return branding;
}
